/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */

import { addClassNamesToElement } from "@lexical/utils";
import { removeClassNamesFromElement } from "@lexical/utils";
import type { EditorConfig, LexicalNode, NodeKey, SerializedTextNode, Spread, TextFormatType } from "lexical";
import { TextNode } from "lexical";

import i18n from "../../../i18n";

type SerializedFilledVariableNode = Spread<
    {
        variableName: string;
        variable: string;
        multiBeneficiaryKey?: string;
    },
    SerializedTextNode
>;

/**
 * FilledVariableNode is used in contract validation page, it's variable node that contains beneficiary data
 */
export class FilledVariableNode extends TextNode {
    variableName: string = "";
    variable: string = "";
    multiBeneficiaryKey?: string = "";

    getVariableName(): string {
        const self = this.getLatest();
        return self.variableName;
    }

    setVariableName(value: string) {
        const self = this.getWritable();
        self.variableName = value;
    }

    static getType(): string {
        return "filled-variable";
    }

    static clone(node: FilledVariableNode): FilledVariableNode {
        return new FilledVariableNode(
            node.__text,
            node.variableName,
            node.variable,
            node.multiBeneficiaryKey,
            node.__key,
        );
    }

    constructor(text: string, variableName: string, variable: string, multiBeneficiaryKey?: string, key?: NodeKey) {
        super(text, key);
        this.variableName = variableName;
        this.variable = variable;
        this.multiBeneficiaryKey = multiBeneficiaryKey;
    }

    /**
     * Create variable, set a theme to variable (CSS)
     * if variable has a multi beneficiary key, we add "multi-beneficiary-variable-key" attribute to catch each variable values for different beneficiaries
     * if a variable is empty we add a CSS style
     */
    createDOM(config: EditorConfig): HTMLElement {
        const element = super.createDOM(config);
        addClassNamesToElement(element, config.theme.filledVariable);
        element.setAttribute("title", this.variable);
        element.setAttribute("data-variable-name", this.variable);
        if (this.multiBeneficiaryKey) {
            element.setAttribute("multi-beneficiary-variable-key", this.multiBeneficiaryKey);
        }
        if (!this.variableName) {
            addClassNamesToElement(element, "PlaygroundEditorTheme__emptyVariable");
            element.setAttribute("title", i18n.t("RichEditor.EmptyVariable"));
        }
        return element;
    }

    /**
     * Update variable, this step is important, it update the variable when editing it
     */
    updateDOM(prevNode: any, dom: HTMLElement, config: EditorConfig): boolean {
        dom.setAttribute("title", this.variable);
        if (!this.variableName) {
            addClassNamesToElement(dom, "PlaygroundEditorTheme__emptyVariable");
        } else {
            removeClassNamesFromElement(dom, "PlaygroundEditorTheme__emptyVariable");
        }
        // Apply toggle styles from toolbar
        const format = Number(this.getFormat());
        dom.style.fontWeight = format === 1 ? "bold" : "lighter";

        super.updateDOM(prevNode, dom, config);
        return false;
    }

    static importJSON(serializedNode: SerializedFilledVariableNode): FilledVariableNode {
        const node = $createFilledVariableNode(
            serializedNode.text,
            serializedNode.variableName,
            serializedNode.variable,
            serializedNode.multiBeneficiaryKey,
        );
        node.setFormat(serializedNode.format);
        node.setDetail(serializedNode.detail);
        node.setMode(serializedNode.mode);
        node.setStyle(serializedNode.style);
        return node;
    }

    exportJSON(): SerializedFilledVariableNode {
        return {
            ...super.exportJSON(),
            variableName: this.variableName,
            variable: this.variable,
            type: "filled-variable",
            multiBeneficiaryKey: this.multiBeneficiaryKey,
        };
    }

    canInsertTextBefore(): boolean {
        return false;
    }

    isTextEntity(): true {
        return true;
    }

    setFormat(format: number | TextFormatType): this {
        super.setFormat(format);
        return this;
    }

    getFormat(): number {
        return super.getFormat();
    }
}

/**
 * Generates a FilledVariableNode, which is a string following the format of a # followed by some text, eg. #lexical.
 * @param text - The text used inside the FilledVariableNode.
 * @param multiBeneficiaryKey - Used with multi-signature it add a key for each beneficiary to the variable
 * @returns - The FilledVariableNode with the embedded text.
 */
export function $createFilledVariableNode(
    text = "",
    variableName = "",
    variable = "",
    multiBeneficiaryKey = "",
    format: number | TextFormatType = 0,
): FilledVariableNode {
    const node = new FilledVariableNode(text, variableName, variable, multiBeneficiaryKey);
    node.setFormat(format);
    return node;
}

/**
 * Determines if node is a FilledVariableNode.
 * @param node - The node to be checked.
 * @returns true if node is a FilledVariableNode, false otherwise.
 */
export function $isFilledVariableNode(node: LexicalNode | null | undefined): node is FilledVariableNode {
    return node instanceof FilledVariableNode;
}
